'use client';
// Forcing recompile for Navbar updates

import { useState, useEffect } from 'react';
import Link from '@/shims/link';
import Image from '@/shims/image';
import { usePathname } from '@/shims/navigation';
import {
    ChevronDown,
    Home,
    Briefcase,
    Layers,
    Calculator,
    Info,
    Phone,
    Globe,
    Building2,
    Plane,
    UserCheck,
    CreditCard,
    MapPin,
    BarChart3,
    FileSearch,
    TrendingUp,
    Percent,
    Landmark,
    Receipt,
    Building,
    Users,
    ShieldCheck,
    FileText,
    Banknote,
    Wallet,
    Anchor,
    Facebook,
    Instagram,
    Linkedin,
    Mail
} from 'lucide-react';
import styles from './Navbar.module.css';
import AnimatedCTAButton from './AnimatedCTAButton';
import GoogleTranslate from './GoogleTranslate';


interface NavSubItem {
    name: string;
    href: string;
    icon: any;
}

interface NavCategory {
    title: string;
    items: NavSubItem[];
}

interface NavItem {
    name: string;
    href?: string;
    icon?: any;
    categories?: NavCategory[];
    children?: NavSubItem[];
}

const navItems: NavItem[] = [
    {
        name: 'Business Setup',
        icon: Briefcase,
        categories: [
            {
                title: 'Company Formation',
                items: [
                    { name: 'Freezone Company', href: '/business-setup/freezone', icon: Globe },
                    { name: 'Mainland Company', href: '/business-setup/mainland', icon: Building2 },
                    { name: 'Offshore Company', href: '/business-setup/offshore', icon: Plane },
                ],
            }
        ],
    },
    { name: 'UK', href: '/uk', icon: Landmark },
    {
        name: 'Services',
        icon: Layers,
        categories: [
            {
                title: 'PRO Services',
                items: [
                    { name: 'PRO Services', href: '/services/pro', icon: UserCheck },
                    { name: 'Office Solutions', href: '/services/office', icon: MapPin },
                ],
            },
            {
                title: 'Banking',
                items: [
                    { name: 'Bank Account Opening', href: '/services/bank-account-opening-in-uae', icon: CreditCard },
                    { name: 'SME Banking', href: '/services/sme-banking', icon: Banknote },
                    { name: 'Corporate Banking', href: '/services/corporate-banking', icon: Landmark },
                    { name: 'Retail Banking', href: '/services/retail-banking', icon: Wallet },
                    { name: 'Mortgage Finance', href: '/services/mortgage-finance', icon: Home },
                ],
            },
            {
                title: 'Tax & Accounting',
                items: [
                    { name: 'Accounting & Bookkeeping', href: '/services/accounting-bookkeeping', icon: BarChart3 },
                    { name: 'Corporate Tax', href: '/services/corporate-tax', icon: Receipt },
                    { name: 'VAT Consultancy', href: '/services/vat-consultancy', icon: Percent },
                ],
            },
            {
                title: 'Advisory',
                items: [
                    { name: 'Audit & Advisory', href: '/services/audit-and-advisory', icon: FileSearch },
                    { name: 'Restructuring & CFO', href: '/services/business-restructuring-and-cfo', icon: Users },
                    { name: 'Corporate Finance', href: '/services/corporate-finance-advisory', icon: TrendingUp },
                ],
            },
        ],
    },
    { name: 'Cost Calculator', href: '/business-setup/freezone/cost-calculator', icon: Calculator },
    {
        name: 'All Zones',
        icon: Globe,
        categories: [
            {
                title: 'Dubai Freezones',
                items: [
                    { name: 'IFZA Dubai', href: '/freezone/dubai/ifza', icon: Globe },
                    { name: 'DMCC', href: '/freezone/dubai/dmcc', icon: Building2 },
                    { name: 'Meydan Free Zone', href: '/freezone/dubai/meydan', icon: Landmark },
                    { name: 'DAFZA', href: '/freezone/dubai/dafza', icon: Plane },
                    { name: 'DIFC', href: '/freezone/dubai/difc', icon: Building },
                    { name: 'Dubai South', href: '/freezone/dubai/dubai-south', icon: Plane },
                    { name: 'View All Dubai', href: '/business-setup/freezone', icon: Layers },
                ],
            },
            {
                title: 'Sharjah & Northern',
                items: [
                    { name: 'Shams Sharjah', href: '/freezone/sharjah/shams', icon: FileText },
                    { name: 'SPC Free Zone', href: '/freezone/sharjah/spc-free-zone', icon: Layers },
                    { name: 'RAKEZ (Ras Al Khaimah)', href: '/freezone/ras-al-khaimah/rakez', icon: FileSearch },
                    { name: 'Fujeirah Creative City', href: '/freezone/fujairah/fujairah-creative-city', icon: Anchor },
                    { name: 'Ajman Free Zone', href: '/freezone/ajman/ajman-free-zone', icon: Building2 },
                ],
            },
            {
                title: 'Abu Dhabi & Others',
                items: [
                    { name: 'KIZAD Abu Dhabi', href: '/freezone/abu-dhabi/kizad', icon: Building },
                    { name: 'ADGM Abu Dhabi', href: '/freezone/abu-dhabi/adgm', icon: Briefcase },
                    { name: 'UAQ Free Trade', href: '/freezone/umm-al-quwain/uaq-free-trade-zone', icon: Globe },
                ],
            },
        ],
    },
    {
        name: 'Company',
        icon: Building,
        children: [
            { name: 'About', href: '/about', icon: Info },
            { name: 'Blog', href: '/blog', icon: FileText },
            { name: 'Contact', href: '/contact', icon: Phone },
        ],
    },
];

export default function Navbar() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
    const [mobileCategoryExpanded, setMobileCategoryExpanded] = useState<string | null>(null);
    const pathname = usePathname();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleMobile = () => setIsMobileOpen(!isMobileOpen);

    const toggleMobileExpand = (name: string) => {
        setMobileExpanded(mobileExpanded === name ? null : name);
        setMobileCategoryExpanded(null);
    };

    const toggleMobileCategory = (title: string) => {
        setMobileCategoryExpanded(mobileCategoryExpanded === title ? null : title);
    };

    return (
        <header className={`${styles.header} ${isScrolled ? styles.scrolled : ''}`}>
            <div className={`container ${styles.navContainer}`}>
                {/* Logo */}
                <Link href="/" className={styles.logo} aria-label="RotanaBiz Home">
                    <Image
                        src="/rotanabiz-logo-small-optimized.webp"
                        alt="RotanaBiz Logo"
                        width={160}
                        height={45}
                        className={styles.logoImage}
                        priority
                    />
                </Link>

                {/* Desktop Nav */}
                <nav className={styles.navLinks}>
                    {navItems.map((item) => (
                        <div key={item.name} className={styles.navItem}>
                            {item.categories ? (
                                <>
                                    <span className={`${styles.link} ${pathname?.startsWith(item.href || '###') ? styles.activeLink : ''}`}>
                                        {item.name} <ChevronDown size={14} />
                                    </span>
                                    <div className={styles.megaMenu}>
                                        <div className={styles.megaMenuInner}>
                                            {item.categories.map((category) => (
                                                <div key={category.title} className={styles.megaCategory}>
                                                    <h4 className={styles.megaCategoryTitle}>{category.title}</h4>
                                                    <div className={styles.megaCategoryItems}>
                                                        {category.items.map((subItem) => (
                                                            <Link
                                                                key={subItem.href}
                                                                href={subItem.href}
                                                                className={styles.megaItem}
                                                            >
                                                                <subItem.icon size={18} />
                                                                <span>{subItem.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            ) : item.children ? (
                                <>
                                    <span className={`${styles.link} ${pathname?.startsWith(item.href || '###') ? styles.activeLink : ''}`}>
                                        {item.name} <ChevronDown size={14} />
                                    </span>
                                    <div className={styles.dropdownMenu}>
                                        {item.children.map((child) => (
                                            <Link key={child.href} href={child.href} className={styles.dropdownItem}>
                                                <child.icon size={16} /> {child.name}
                                            </Link>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <Link
                                    href={item.href!}
                                    className={`${styles.link} ${pathname === item.href ? styles.activeLink : ''}`}
                                >
                                    {item.name}
                                </Link>
                            )}
                        </div>
                    ))}
                    <GoogleTranslate />
                    <AnimatedCTAButton href="/contact" />
                </nav>

                {/* Mobile Toggle */}
                <button className={styles.mobileToggle} onClick={toggleMobile} aria-label="Toggle Menu">
                    {isMobileOpen ? '✕' : '☰'}
                </button>

                {/* Mobile Nav */}
                <div className={`${styles.mobileMenu} ${isMobileOpen ? styles.mobileMenuOpen : ''}`}>
                    {navItems.map((item) => (
                        <div key={item.name} className={styles.mobileLinkWrapper}>
                            {item.categories ? (
                                <>
                                    <button
                                        className={styles.mobileLink}
                                        onClick={() => toggleMobileExpand(item.name)}
                                        style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', padding: 0 }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {item.icon && <item.icon size={20} />} {item.name}
                                        </span>
                                        <ChevronDown
                                            size={20}
                                            style={{
                                                transform: mobileExpanded === item.name ? 'rotate(180deg)' : 'rotate(0)',
                                                transition: 'transform 0.3s'
                                            }}
                                        />
                                    </button>
                                    {mobileExpanded === item.name && (
                                        <div className={styles.mobileCategories}>
                                            {item.categories.map((category) => (
                                                <div key={category.title} className={styles.mobileCategoryGroup}>
                                                    <button
                                                        className={styles.mobileCategoryTitle}
                                                        onClick={() => toggleMobileCategory(category.title)}
                                                    >
                                                        {category.title}
                                                        <ChevronDown
                                                            size={16}
                                                            style={{
                                                                transform: mobileCategoryExpanded === category.title ? 'rotate(180deg)' : 'rotate(0)',
                                                                transition: 'transform 0.3s'
                                                            }}
                                                        />
                                                    </button>
                                                    {mobileCategoryExpanded === category.title && (
                                                        <div className={styles.mobileDropdown}>
                                                            {category.items.map((subItem) => (
                                                                <Link
                                                                    key={subItem.href}
                                                                    href={subItem.href}
                                                                    className={styles.mobileSubLink}
                                                                    onClick={() => setIsMobileOpen(false)}
                                                                >
                                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                        <subItem.icon size={18} /> {subItem.name}
                                                                    </span>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : item.children ? (
                                <>
                                    <button
                                        className={styles.mobileLink}
                                        onClick={() => toggleMobileExpand(item.name)}
                                        style={{ background: 'none', border: 'none', width: '100%', cursor: 'pointer', padding: 0 }}
                                    >
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {item.icon && <item.icon size={20} />} {item.name}
                                        </span>
                                        <ChevronDown
                                            size={20}
                                            style={{
                                                transform: mobileExpanded === item.name ? 'rotate(180deg)' : 'rotate(0)',
                                                transition: 'transform 0.3s'
                                            }}
                                        />
                                    </button>
                                    {mobileExpanded === item.name && (
                                        <div className={styles.mobileDropdown}>
                                            {item.children.map((child) => (
                                                <Link
                                                    key={child.href}
                                                    href={child.href}
                                                    className={styles.mobileSubLink}
                                                    onClick={() => setIsMobileOpen(false)}
                                                >
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <child.icon size={18} /> {child.name}
                                                    </span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link
                                    href={item.href!}
                                    className={styles.mobileLink}
                                    onClick={() => setIsMobileOpen(false)}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {item.icon && <item.icon size={20} />} {item.name}
                                    </span>
                                </Link>
                            )}
                        </div>
                    ))}
                    <GoogleTranslate />
                    <AnimatedCTAButton
                        href="/contact"
                        onClick={() => setIsMobileOpen(false)}
                        className={styles.mobileCta}
                    />

                    {/* Mobile Contact & Socials */}
                    <div className={styles.mobileFooter}>
                        <div className={styles.mobileContactList}>
                            <a href="mailto:info@rotanabiz.com" className={styles.mobileContactItem}>
                                <Mail size={16} /> info@rotanabiz.com
                            </a>
                            <a href="tel:+97172430438" className={styles.mobileContactItem}>
                                <Phone size={16} /> +971 7 2430438
                            </a>
                        </div>
                        <div className={styles.mobileSocials}>
                            <a href="https://www.linkedin.com/company/rotana-biz" target="_blank" rel="noopener noreferrer" className={styles.mobileSocialLink} aria-label="LinkedIn">
                                <Linkedin size={20} />
                            </a>
                            <a href="https://www.instagram.com/rotanabiz/" target="_blank" rel="noopener noreferrer" className={styles.mobileSocialLink} aria-label="Instagram">
                                <Instagram size={20} />
                            </a>
                            <a href="https://www.facebook.com/RotanaBiz" target="_blank" rel="noopener noreferrer" className={styles.mobileSocialLink} aria-label="Facebook">
                                <Facebook size={20} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </header>
    );
}
